import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useParams } from 'react-router-dom';
import clsx from 'clsx';
import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import Avatar from '@material-ui/core/Avatar';
import Grid from '@material-ui/core/Grid';
import DialogContentText from '@material-ui/core/DialogContentText';
import DeleteIcon from '@material-ui/icons/Delete';
import { useTranslation } from 'react-i18next';
import flattenDeep from 'lodash/flattenDeep';
import Card from 'eventtia-ui-components/lib/Card';
import Input from 'eventtia-ui-components/lib/Input';
import PhoneInput from 'eventtia-ui-components/lib/PhoneInput';
import Button from 'eventtia-ui-components/lib/Button';
import Loader from 'eventtia-ui-components/lib/Loader';
import Message from 'eventtia-ui-components/lib/Message';
import Modal from 'eventtia-ui-components/lib/Modal';
import Dialog from 'eventtia-ui-components/lib/Dialog';
import SelectInput from 'eventtia-ui-components/lib/SelectInput';
import { compose } from 'redux';
import { connect } from 'react-redux';
import useDidUpdate from '../../hooks/useDidUpdate';
import withForm from '../../hocs/withForm';
import CustomPropTypes from '../../helpers/CustomPropTypes';
import { deleteAttendeeLead } from '../../actions/attendeeLeads';
import { setActiveLead } from '../../actions/app';

const extractMessages = (raw) => {
  const start = raw.indexOf('[');
  const end = raw.lastIndexOf(']');
  if (start === -1 || end === -1) return [];
  return raw.slice(start + 1, end).split('", "').map((entry, index, entries) => {
    let message = entry;
    if (index === 0 && message.startsWith('"')) message = message.slice(1);
    if (index === entries.length - 1 && message.endsWith('"')) message = message.slice(0, -1);
    return message;
  });
};

const REQUIRED_SUFFIX = ' is required';
const formatFieldError = (message, t) => {
  if (!message.endsWith(REQUIRED_SUFFIX)) return message;
  const field = message.slice(0, message.length - REQUIRED_SUFFIX.length);
  return t('attendeeLeads:formError.requiredField', { field });
};

const parseServerError = (error, t) => {
  const messages = Array.isArray(error) ? flattenDeep(error) : extractMessages(String(error));
  if (!messages.length) return error;
  return messages
    .map((message) => formatFieldError(message, t))
    .reduce((acc, message) => (
      acc.length === 0 ? [message] : [...acc, <br key={`break-${message}`} />, message]
    ), []);
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(2, 4),
  },
  card: {
    marginTop: theme.spacing(1),
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    margin: theme.spacing(0, 0, 2.5, 0),
    width: '100%',
    position: 'relative',
  },
  avatar: {
    height: 70,
    width: 70,
    marginRight: theme.spacing(2),
    backgroundColor: theme.palette.lightGrey.main,
    color: theme.palette.primary.main,
  },
  name: {
    fontSize: 16,
    color: theme.palette.darkGrey.dark,
    fontWeight: 'bold',
  },
  job: {
    fontSize: 14,
    color: theme.palette.darkGrey.main,
  },
  deleteButton: {
    color: theme.palette.red.main,
    [theme.breakpoints.down('sm')]: {
      minWidth: '0%',
      padding: theme.spacing(0, 0, 0, 1),
    },
    [theme.breakpoints.down('xs')]: {
      minWidth: '170px',
      padding: 'unset',
    },
  },
  deleteText: {
    [theme.breakpoints.down('sm')]: {
      display: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      display: 'unset',
    },
  },
  saveButton: {
    display: 'block',
    marginLeft: 'auto',
    [theme.breakpoints.down('sm')]: {
      minWidth: theme.spacing(10),
    },
    [theme.breakpoints.down('xs')]: {
      width: '100%',
      minWidth: theme.spacing(21),
    },
  },
  loader: {
    zIndex: 3,
  },
  message: {
    marginBottom: theme.spacing(2),
  },
  customFieldLabel: {
    '& label': {
      display: '-webkit-box',
      WebkitBoxOrient: 'vertical',
      WebkitLineClamp: 2,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      wordBreak: 'break-word',
      cursor: 'help',
      transition: 'all 0.2s ease',
    },
    '&:hover label, &:focus-within label': {
      WebkitLineClamp: 'unset',
      overflow: 'visible',
    },
  },
  selectMenuPaper: {
    maxWidth: 'min-content !important',
    '& .MuiMenuItem-root, & .MuiFormControlLabel-label': {
      whiteSpace: 'normal',
      wordBreak: 'break-word',
    },
  },
  form: {
    alignItems: 'flex-end',
  },
  actionsContainer: {
    display: 'flex',
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: theme.spacing(6),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column-reverse',
    },
  },
  actions: {
    display: 'flex',
    justifyContent: 'flex-end',
    alignItems: 'center',
    gap: theme.spacing(2),
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1),
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
    },
  },
  actionsContainerNewLead: {
    justifyContent: 'flex-end',
  },
  confirmDeleteText: {
    padding: theme.spacing(3),
  },
  dialog: {
    textAlign: 'center',
    '& > div.MuiDialogActions-root': {
      flexWrap: 'nowrap !important',
    },
  },
}));

const isEmptyValue = (value) => (Array.isArray(value) ? !value.length : !value);

const AttendeeLeadShow = ({
  attendeeLead, registerInput, watchFormValues, setFormValue, setError, clearError,
  errors: formErrors, updating, success, submitError, leadFields, new: isNewLead,
  leadFieldsSuccess, leadFieldsOrder, deleteAttendeeLead: dispatchDeleteAttendeeLead,
  setActiveLead: dispatchSetActiveLead,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('global');
  const [message, setMessage] = useState();
  const [confirmDelete, setConfirmDelete] = useState(false);
  useDidUpdate(() => {
    if (success) setMessage({ type: 'success', text: t('attendeeLeads:info.savedSuccessfully') });
    else if (submitError) setMessage({ type: 'error', text: parseServerError(submitError, t) });
  }, [success, submitError]);

  const containerRef = useRef(null);
  const { eventUri } = useParams();

  const phone = watchFormValues('attendee[telephone]') || attendeeLead.phone;
  const allValues = watchFormValues() || {};

  useEffect(() => {
    registerInput({ name: 'phone' });
  }, [registerInput]);

  useEffect(() => {
    if (leadFieldsSuccess) {
      const defaults = attendeeLead.customFieldsData || {};
      Object.values(leadFields).forEach(({ id, inputType: type }) => {
        if ([3, 4].includes(type)) registerInput({ name: `customFieldsData._${id}` });
        setFormValue(`customFieldsData._${id}`, defaults[id]);
      });
    }
  }, [leadFieldsSuccess, leadFields]);

  const orderedFields = leadFieldsOrder?.map((id) => leadFields[id])
    .filter((field) => field?.id);

  // TODO: review registerInput second argument and validations,
  // as it is not working for custom fields, and we are using setError instead
  // this changes affect some forms in proyect that ignore the required field validation.

  const requiredFieldNames = [
    'fullName',
    'email',
    ...(orderedFields || [])
      .filter(({ options: { required } = {} }) => required)
      .map(({ id }) => `customFieldsData._${id}`),
  ];

  const checkRequiredFields = () => {
    const missing = requiredFieldNames.filter((name) => isEmptyValue(allValues?.[name]));
    clearError(requiredFieldNames);
    missing.forEach((name) => setError(name, 'required', t('formError.requiredField')));
    return missing;
  };

  const customField = (field) => {
    const {
      id, name, inputType: type, values,
      options: { required } = {},
    } = field;
    const key = `customFieldsData._${id}`;
    const fieldError = formErrors?.customFieldsData?.[`_${id}`]?.message;

    const formattedValues = values && values.map((val) => ({ value: val, label: val }));
    const validations = required ? {
      required: { value: true, message: t('formError.requiredField') },
    } : {};

    switch (type) {
      case 2:
        return (
          <Input
            id={key}
            name={key}
            label={name}
            required={required}
            error={fieldError}
            inputRef={registerInput(validations)}
            containerClassName={classes.customFieldLabel}
            multiline
            rows={3}
            rowsMax={6}
          />
        );
      case 3:
        return (
          <SelectInput
            id={key}
            value={allValues?.[key] || []}
            label={name}
            required={required}
            options={formattedValues}
            handleChange={(val) => { setFormValue(key, val); }}
            error={fieldError}
            containerClassName={classes.customFieldLabel}
            MenuProps={{ classes: { paper: classes.selectMenuPaper } }}
            multiple
          />
        );
      case 4:
        return (
          <SelectInput
            id={key}
            label={name}
            required={required}
            options={formattedValues}
            handleChange={(val) => { setFormValue(key, val); }}
            value={allValues?.[key] || ''}
            error={fieldError}
            containerClassName={classes.customFieldLabel}
            MenuProps={{ classes: { paper: classes.selectMenuPaper } }}
          />
        );
      default:
        return (
          <Input
            id={key}
            name={key}
            label={name}
            required={required}
            error={fieldError}
            inputRef={registerInput(validations)}
            containerClassName={classes.customFieldLabel}
          />
        );
    }
  };

  return (
    <div className={classes.root} ref={containerRef}>
      <Card className={classes.card}>
        <Loader loading={updating} variant="absolute" className={classes.loader} />

        <Message
          className={classes.message}
          type={message?.type}
          onClose={() => setMessage({ type: message?.type })}
          message={message?.text}
        />

        <div className={classes.header}>
          <Avatar className={classes.avatar} />
          <div>
            <Typography className={classes.name} variant="subtitle1">
              {attendeeLead.fullName}
            </Typography>
            {attendeeLead.company && (
              <Typography className={classes.job} variant="body2">
                {attendeeLead.company}
              </Typography>
            )}
          </div>
        </div>

        {/* to preserve the uuid */}
        <input
          id="uuid"
          name="uuid"
          ref={registerInput()}
          type="hidden"
        />

        <Grid container spacing={2} className={classes.form}>
          <Grid item xs={12} md={6}>
            <Input
              id="fullName"
              name="fullName"
              placeholder={t('forms.fullName')}
              label={t('forms.fullName')}
              error={formErrors?.fullName?.message}
              inputRef={registerInput({
                required: { value: true, message: t('formError.requiredField') },
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              id="email"
              name="email"
              placeholder={t('forms.email')}
              label={t('forms.email')}
              error={formErrors?.email?.message}
              inputRef={registerInput({
                required: { value: true, message: t('formError.requiredField') },
              })}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <Input
              id="company"
              name="company"
              placeholder={t('forms.company')}
              label={t('forms.company')}
              error={formErrors?.company?.message}
              inputRef={registerInput()}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <PhoneInput
              id="phone"
              name="phone"
              placeholder={t('forms.telephone')}
              label={t('forms.telephone')}
              value={phone || ''}
              handleChange={(val) => setFormValue('phone', val)}
              error={formErrors?.phone?.message}
            />
          </Grid>
          <Grid item xs={12}>
            <Input
              id="notes"
              name="notes"
              placeholder={t('forms.notes')}
              label={t('forms.notes')}
              error={formErrors?.notes?.message}
              inputRef={registerInput()}
              multiline
              rows={3}
              rowsMax={10}
            />
          </Grid>
          {!!leadFieldsSuccess && orderedFields?.map((field, index) => {
            const f = customField(field, index);
            return (
              <Grid item xs={12} key={field.id}>
                {f}
              </Grid>
            );
          })}
        </Grid>

        <div
          className={clsx(
            classes.actionsContainer,
            isNewLead && classes.actionsContainerNewLead
          )}
        >
          {!isNewLead && (
            <Button
              variant="tertiary"
              className={classes.deleteButton}
              icon={<DeleteIcon />}
              onClick={() => setConfirmDelete(true)}
            >
              <span className={classes.deleteText}>
                {t('attendeeLeads:actions.deleteLead')}
              </span>
            </Button>
          )}
          <div className={classes.actions}>
            <Button
              type="submit"
              disabled={updating}
              className={classes.saveButton}
              onClick={(event) => {
                if (checkRequiredFields().length) event.preventDefault();
                if (containerRef.current) containerRef.current.scrollIntoView({ block: 'start' });
              }}
            >
              {isNewLead ? t('actions.create') : t('attendeeLeads:actions.save')}
            </Button>
            <Button
              variant="secondary"
              disabled={updating}
              className={classes.saveButton}
              onClick={() => dispatchSetActiveLead()}
            >
              {t('attendeeLeads:actions.cancel')}
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        open={confirmDelete}
        handleClose={() => setConfirmDelete(false)}
        PaperProps={{ className: classes.dialog }}
      >
        <Dialog
          title={t('attendeeLeads:actions.deleteLead')}
          handleClose={() => setConfirmDelete(false)}
          action={{
            name: t('attendeeLeads:actions.deleteLead'),
            onClick: () => {
              dispatchDeleteAttendeeLead(attendeeLead, eventUri);
              dispatchSetActiveLead();
              setConfirmDelete(false);
            },
          }}
          secondaryAction={{
            name: t('attendeeLeads:actions.cancel'),
            onClick: () => setConfirmDelete(false),
          }}
        >
          <DialogContentText className={classes.confirmDeleteText}>
            {t('attendeeLeads:info.confirmDeleteLead')}
          </DialogContentText>
        </Dialog>
      </Modal>
    </div>
  );
};

AttendeeLeadShow.propTypes = {
  attendeeLead: CustomPropTypes.attendeeLead.isRequired,
  registerInput: PropTypes.func.isRequired,
  watchFormValues: PropTypes.func.isRequired,
  setFormValue: PropTypes.func.isRequired,
  setError: PropTypes.func.isRequired,
  clearError: PropTypes.func.isRequired,
  errors: CustomPropTypes.formErrors.isRequired,
  updating: PropTypes.bool.isRequired,
  success: PropTypes.bool.isRequired,
  submitError: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]),
  deleteAttendeeLead: PropTypes.func.isRequired,
  setActiveLead: PropTypes.func.isRequired,
  leadFields: PropTypes.objectOf(PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    name: PropTypes.string,
    inputType: PropTypes.number,
    values: PropTypes.arrayOf(PropTypes.string),
    options: PropTypes.shape({ required: PropTypes.bool }),
  })),
  leadFieldsSuccess: PropTypes.bool.isRequired,
  leadFieldsOrder: PropTypes.arrayOf(PropTypes.string),
  new: PropTypes.bool.isRequired,
};

AttendeeLeadShow.defaultProps = {
  leadFields: {},
  submitError: null,
  leadFieldsOrder: undefined,
};

const mapStateToProps = ({
  entities: {
    customFieldsCustomFields,
  },
  fetchStatus: {
    createOrUpdateAttendeeLead: {
      isFetching,
      success,
      error: submitError,
    },
    attendeeLeadsFields: {
      success: leadFieldsSuccess,
      order: leadFieldsOrder,
    },
  },
}) => ({
  updating: isFetching,
  success,
  submitError,
  leadFields: customFieldsCustomFields,
  leadFieldsSuccess,
  leadFieldsOrder,
});

export default compose(
  withForm({ required: false, autoComplete: 'off' }),
  connect(mapStateToProps, {
    deleteAttendeeLead, setActiveLead,
  })
)(AttendeeLeadShow);
